type Item = {
  guid: string;
};

const MAX_CONCURRENT_API_REQUESTS = 6;
let activeApiRequests = 0;
const apiRequestQueue: Array<() => void> = [];

async function acquireApiRequestSlot(): Promise<void> {
  if (activeApiRequests < MAX_CONCURRENT_API_REQUESTS) {
    activeApiRequests += 1;
    return;
  }

  await new Promise<void>((resolve) => {
    apiRequestQueue.push(resolve);
  });
  activeApiRequests += 1;
}

function releaseApiRequestSlot(): void {
  activeApiRequests = Math.max(0, activeApiRequests - 1);
  apiRequestQueue.shift()?.();
}

async function limitedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  await acquireApiRequestSlot();
  try {
    return await fetch(input, init);
  } finally {
    releaseApiRequestSlot();
  }
}

export default class API<T extends Item> {
  baseUrl: string;
  entity: string;
  constructor(entity: string, version?: string) {
    this.baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/${version ?? "v1"}/`;
    this.entity = entity;
  }

  async getIds(params?: URLSearchParams): Promise<Array<string>> {
    const res = await limitedFetch(`${this.baseUrl}${this.entity}?${params}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      throw error;
    }

    return (await res.json()) as Array<string>;
  }

  async getOne(id: string): Promise<T> {
    const res = await limitedFetch(`${this.baseUrl}${this.entity}/${id}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      throw error;
    }

    return (await res.json()) as T;
  }

  getMany(ids: Array<string>): Promise<Array<T>> {
    const results = ids.map((id) => this.getOne(id));
    return Promise.all(results);
  }

  async updateOne(item: T): Promise<Item> {
    const { guid, ...payload } = item; // removing guid from stock class, not required when updating
    const url = `${this.baseUrl}${this.entity}/${item.guid}`;
    const res = await limitedFetch(url, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      throw error;
    }

    const result = (await res.json()) as Item;
    return result;
  }
  async createOne(item: T): Promise<Item> {
    const { guid, ...payload } = item; // removing guid from stock class, not required when updating
    const url = `${this.baseUrl}${this.entity}`;
    const res = await limitedFetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      throw error;
    }

    const result = (await res.json()) as Item;
    return result;
  }
  async createOneWithIdempotency(item: T): Promise<Item> {
    const { guid } = item;
    const url = `${this.baseUrl}${this.entity}`;
    const res = await limitedFetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
        "Idempotency-Key": guid,
      },
      body: JSON.stringify(item),
    });

    if (res.status === 304) {
      return { guid } as Item;
    }

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }));
      throw error;
    }

    const result = (await res.json()) as Item;
    return result;
  }
  async deleteOne(id: string) {
    const url = `${this.baseUrl}${this.entity}/${id}`;
    return limitedFetch(url, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });
  }
}

export const fetchApi = async (
  relativeUrl: string,
  init?: RequestInit,
  version: string = "v1"
) => {
  const baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/${version}/`;
  const res = await limitedFetch(`${baseUrl}${relativeUrl}`, {
    ...init,
    headers: {
      ...init?.headers,
      Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
      "Content-Type": "application/json",
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw error;
  }

  // Check if response has content before trying to parse JSON
  // DELETE requests and 204 No Content responses typically have no body
  const contentType = res.headers.get("content-type");
  const hasJsonContent = contentType?.includes("application/json");

  // If 204 No Content or no JSON content-type, return undefined
  if (res.status === 204 || !hasJsonContent) {
    return undefined;
  }

  // Try to parse JSON, but handle empty responses gracefully
  const text = await res.text();
  if (!text || text.trim() === "") {
    return undefined;
  }

  return JSON.parse(text) as unknown;
};
